<script lang="ts">
  import { onDestroy } from 'svelte';
  import type { VoiceType } from 'shared/definitions/musicTheory/voiceTypes';
  import { vocalRanges } from 'shared/definitions/musicTheory/voiceTypes';

  // import wasmUrl from 'wasm-pitch/releases/wasm_interface.wasm?url';
  import WasmPitch from 'wasm-pitch/src/wasm-pitch';

  import Note from '../../uTheory.Notation/note';
  import { SpicePitch } from '../../uTheory.Notation/pitch-detection/spice';

  import GameBoard from './GameBoard.svelte';
  // import { AutoCorrelatePitch } from '../../uTheory.Notation/pitch-detection/autocorrelation';
  import PitchDetectionAverager from '../../uTheory.Notation/pitchDetectionAverager';
  import { PitchyFishGameStore } from './PitchyFishGameStore';
  import { getMicrophonePermissionState } from '../../uTheory.Notation/pitch-detection/microphonePermissionCheck';
  import UPrefs from '../uPrefs';
  import errorProvider from '../../errorProvider';

  const uPrefs = new UPrefs();

  let voiceType: VoiceType = 'mezzo-soprano';

  let midi: number = -1;
  const store = new PitchyFishGameStore({ highScore: 0 });

  // If a high score has been saved on the server, set it to that:
  uPrefs.setAppName('pitchy-fish', undefined, errorProvider.error).then(() => {
    store.highScore = uPrefs.getPreference('userHighScore') || 0;
  });

  const pitchDetectionAverager = new PitchDetectionAverager({ timeWindowSeconds: 0.4 });
  // const spiceDetectionAverager = new PitchDetectionAverager({ timeWindowSeconds: 0.4 });

  let datum: { frequency: number; confidence: number } = {
    frequency: -1,
    confidence: 1,
  };

  let regression: ReturnType<PitchDetectionAverager['addMidi']>;
  // let spiceRegression: ReturnType<PitchDetectionAverager['addMidi']>;

  const minimumConfidence: number = 0.2;

  const spicePitch = new SpicePitch();
  spicePitch.addPitchDetectedCallback((data) => {
    datum = data[0]!;
    let dataMidi = -1;
    if (datum.confidence > minimumConfidence) {
      dataMidi = datum.frequency > 55 ? Note.frequencyToMidi(datum.frequency) : -1;
      regression = pitchDetectionAverager.addMidi(dataMidi);
      // spiceRegression = pitchDetectionAverager.addMidi(dataMidi);
    }
    midi = regression?.medianClusterAverage || -1;
  });

  let wasmPitch: WasmPitch | undefined;
  if (typeof window !== 'undefined') {
    // const wasmDirectory = wasmUrl.replace('/wasm_interface.wasm', '');
    const wasmDirectory = '/assets/wasm-pitch';
    wasmPitch = new WasmPitch(wasmDirectory);
    wasmPitch.addListener((freq: number) => {
      regression = pitchDetectionAverager.addFrequency(freq);
      midi = regression.medianClusterAverage;
    });
  }

  // const autocorrelator = new AutoCorrelatePitch();
  // autocorrelator.setSmoothing('extreme');
  // autocorrelator.addPitchDetectedCallback((data) => {
  //   datum = data;
  //   let dataMidi = -1;
  //   if (data.confidence > minimumConfidence) {
  //     dataMidi = data.frequency > 40 ? Note.frequencyToMidi(data.frequency) : -1;
  //   }
  //   const { regressionMidiAverage } = pitchDetectionAverager.addMidi(dataMidi);
  //   midi = regressionMidiAverage;
  // });

  async function startPitchDetection() {
    try {
      await Promise.all([spicePitch.start(), wasmPitch?.init()]);
      wasmPitch?.start();
      store.microphonePermissionStatus = 'granted';
      store.pitchDetectionStatus = 'running';
      store.startGame();
    } catch (err: any) {
      store.microphonePermissionStatus = 'denied';
      store.state = 'microphonePermissionDenied';
      console.error(err);
    }
  }

  onDestroy(() => {
    if (wasmPitch?.isRunning) {
      wasmPitch?.stop();
    }
    spicePitch.stop();
  });

  async function checkForMicPermission() {
    try {
      const permissionStatus = await getMicrophonePermissionState();
      store.microphonePermissionStatus = permissionStatus;
    } catch (err: any) {
      store.error = err?.message || 'Unable to check for mic access.';
    }
  }

  checkForMicPermission();

  store.addEventListener('MichrophonePermissionCheckRequest', checkForMicPermission);
  store.addEventListener('StartPitchDetectionRequest', startPitchDetection);
  store.addEventListener('transition', (state) => {
    console.log(`state changed to ${state}`);
    if (state === 'gameOver') {
      wasmPitch?.stop();
      spicePitch.stop();
      store.pitchDetectionStatus = 'notRunning';
      if ($store.score > $store.highScore) {
        store.highScore = $store.score;
        console.log({
          score: $store.score,
          highScore: $store.highScore,
        });
        uPrefs.setPreference('userHighScore', $store.score).catch(errorProvider.error);
      }
    }
  });

  $: {
    store.highestMidi = new Note(vocalRanges[voiceType].highestNoteName).getMidiNumber();
  }
  $: {
    store.lowestMidi = new Note(vocalRanges[voiceType].lowestNoteName).getMidiNumber();
  }
</script>

<GameBoard bind:midi {store} bind:voiceType />

<!-- TODO: Analyze PitchDetectionAverager and experiment with optimized stability. -->
<style>
  @import url('https://fonts.googleapis.com/css2?family=Luckiest+Guy&display=swap');
</style>
